import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Droplets, Sparkles, Wrench, Bug, Thermometer, UserCheck, FileBadge, Loader2 } from 'lucide-react';

function arquivoDaEscolha(event) {
  const arquivo = event.target.files && event.target.files[0];
  if (!arquivo) return null;
  const tipo = arquivo.type || 'image/jpeg';
  return new File([arquivo], arquivo.name || `foto-${Date.now()}.jpg`, {
    type: tipo,
    lastModified: arquivo.lastModified
  });
}

function PreviaFoto({ arquivo }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!arquivo || !String(arquivo.type || '').startsWith('image/')) {
      setUrl('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(arquivo);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [arquivo]);
  if (!arquivo) return <p className="mt-1 text-xs text-gray-500">Nenhuma foto escolhida.</p>;
  return (
    <div className="mt-2 flex items-center gap-2">
      {url ? <img src={url} alt="" className="h-16 w-16 rounded object-cover border border-gray-200" /> : null}
      <span className="text-xs text-gray-700 break-all">{arquivo.name}</span>
    </div>
  );
}

function formatarDataHoraBr(valor) {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return String(valor).replace('T', ' ').slice(0, 16);
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(data);
}

const TIPOS = [
  {
    id: 'agua',
    label: 'Potabilidade da água',
    icon: Droplets,
    ajuda: 'pH de 6,0 a 9,5 e cloro residual livre de 0,2 a 5,0 mg/L. Fora da faixa, o registro fica não conforme. Anexe as duas fotos da análise.'
  },
  {
    id: 'limpeza',
    label: 'Limpeza e higienização',
    icon: Sparkles,
    ajuda: 'Pré-operacional: conferir superfície limpa, sem resíduo e sem odor antes de iniciar. Pós-operacional: conferir limpeza após o uso e após manutenção. Selecione a máquina do setor que foi limpa. Marque conforme só se a conferência visual bater com essa regra.'
  },
  {
    id: 'manutencao',
    label: 'Manutenção',
    icon: Wrench,
    ajuda: 'Preventiva segue o plano. Corretiva descreve a falha, o que foi feito e se a área voltou a operar.'
  },
  {
    id: 'pragas',
    label: 'Controle de pragas',
    icon: Bug,
    ajuda: 'Diário: isca, local e se houve presença. Não conforme exige ação na observação.'
  },
  {
    id: 'temperatura',
    label: 'Temperatura',
    icon: Thermometer,
    ajuda: 'Duas vezes ao dia (manhã e tarde). O ponto é uma câmara ou um termohigrômetro já cadastrado em Equipamentos. A foto do visor é obrigatória.'
  },
  {
    id: 'habitos',
    label: 'Hábitos higiênicos',
    icon: UserCheck,
    ajuda: 'Asseio, uniforme e adornos. Conforme quando o colaborador atende ao padrão da empresa.'
  },
  {
    id: 'certificados',
    label: 'Certificados de serviços',
    icon: FileBadge,
    ajuda: 'Calibração, controle de pragas, limpeza de caixa d’água e microbiológico. Guarde validade e o anexo.'
  }
];

const vazio = {
  ponto: '',
  ph: '',
  cloro: '',
  momento: 'pre_operacional',
  alvo: 'setor',
  setor_id: '',
  equipamento_id: '',
  como_conferir: 'Superfície limpa, sem resíduo, sem odor e seca o suficiente para operar.',
  classe: 'preventiva',
  item: 'equipamento',
  descricao: '',
  local: '',
  presenca: 'nao',
  valor: '',
  turno: 'manha',
  colaborador: '',
  asseio: 'sim',
  uniforme: 'sim',
  servico: 'calibracao',
  fornecedor: '',
  validade: '',
  observacao: '',
  conforme: true
};

const MonitoramentoPage = () => {
  const { userData, companyId, getEmpresaId, isRole } = useAuth();
  const empresaId = companyId || getEmpresaId?.();
  const [tipo, setTipo] = useState('agua');
  const [form, setForm] = useState(vazio);
  const [foto, setFoto] = useState(null);
  const [fotoAgua1, setFotoAgua1] = useState(null);
  const [fotoAgua2, setFotoAgua2] = useState(null);
  const [setores, setSetores] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');

  const atual = useMemo(() => TIPOS.find((t) => t.id === tipo), [tipo]);
  const equipamentosDoSetor = useMemo(() => {
    if (!form.setor_id) return [];
    const setor = setores.find((s) => s.id === form.setor_id);
    const ids = new Set(setor?.equipamento_ids || []);
    return equipamentos.filter((eq) => ids.has(eq.id) || eq.setor_id === form.setor_id);
  }, [equipamentos, form.setor_id, setores]);
  const equipamentosTemperatura = useMemo(
    () =>
      equipamentos.filter((eq) =>
        /c[aâ]mara|termohigr|termo higr|termo-higr|higr[oô]metro/i.test(
          `${eq.tipo_nome || ''} ${eq.apelido || ''} ${eq.nome || ''}`
        )
      ),
    [equipamentos]
  );

  const carregar = async (tipoAtual = tipo) => {
    if (!empresaId || empresaId === 'default') {
      setLoading(false);
      setErro('Empresa não identificada neste usuário.');
      return;
    }
    setLoading(true);
    setErro('');
    try {
      const list = httpsCallable(functions, 'listMonitoramentoRegistros');
      const r = await list({ empresa_id: empresaId, tipo: tipoAtual });
      setItens(r.data?.data?.items || []);
    } catch (e) {
      setErro(e.message || 'Não foi possível carregar os registros.');
      setItens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar(tipo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, empresaId]);

  useEffect(() => {
    if (!empresaId || empresaId === 'default') return undefined;
    let cancel = false;
    (async () => {
      try {
        const ls = httpsCallable(functions, 'listSetores');
        const le = httpsCallable(functions, 'listEquipamentos');
        const [s, e] = await Promise.all([
          ls({ empresa_id: empresaId, apenas_ativos: true }),
          le({ empresa_id: empresaId })
        ]);
        if (cancel) return;
        setSetores(s.data?.data?.items || []);
        setEquipamentos(e.data?.data?.items || []);
      } catch {
        /* listas de apoio */
      }
    })();
    return () => {
      cancel = true;
    };
  }, [empresaId]);

  const set = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    setOk('');
    try {
      if (tipo === 'limpeza') {
        if (!form.setor_id) throw new Error('Selecione o setor.');
        if (equipamentosDoSetor.length > 0 && !form.equipamento_id) {
          throw new Error('Selecione a máquina que foi limpa.');
        }
      }
      if (tipo === 'temperatura' && !foto) {
        throw new Error('Anexe a foto do visor ou do laudo.');
      }
      if (tipo === 'agua' && (!fotoAgua1 || !fotoAgua2)) {
        throw new Error('Anexe as duas fotos da análise.');
      }
      const arquivos = tipo === 'agua' ? [fotoAgua1, fotoAgua2] : foto ? [foto] : [];
      const fotos = [];
      for (const arquivo of arquivos) {
        const extensao = String(arquivo.name || '').split('.').pop().toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
        const nome = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${extensao}`;
        const storageRef = ref(storage, `monitoramento/${empresaId}/${nome}`);
        await uploadBytes(storageRef, arquivo, { contentType: arquivo.type || 'image/jpeg' });
        fotos.push(await getDownloadURL(storageRef));
      }
      const foto_url = fotos[0] || '';
      const campos = {};
      const copiar = {
        agua: ['ponto', 'ph', 'cloro'],
        limpeza: ['momento', 'alvo', 'setor_id', 'equipamento_id', 'como_conferir', 'descricao'],
        manutencao: ['classe', 'item', 'equipamento_id', 'descricao'],
        pragas: ['local', 'presenca', 'descricao'],
        temperatura: ['ponto', 'valor', 'turno', 'equipamento_id'],
        habitos: ['colaborador', 'asseio', 'uniforme'],
        certificados: ['servico', 'fornecedor', 'validade']
      }[tipo];
      copiar.forEach((k) => {
        campos[k] = String(form[k] ?? '');
      });
      if (tipo === 'limpeza') {
        const setor = setores.find((s) => s.id === form.setor_id);
        const eq = equipamentos.find((e) => e.id === form.equipamento_id);
        campos.setor_nome = setor?.nome || '';
        campos.equipamento_nome = eq?.apelido || eq?.nome || '';
      }
      if (tipo === 'manutencao' || tipo === 'temperatura') {
        const eq = equipamentos.find((e) => e.id === form.equipamento_id);
        campos.equipamento_nome = eq?.apelido || eq?.nome || '';
      }
      const fn = httpsCallable(functions, 'createMonitoramentoRegistro');
      await fn({
        empresa_id: empresaId,
        tipo,
        campos,
        foto_url,
        fotos,
        conforme: form.conforme === true,
        observacao: form.observacao
      });
      setOk('Registro salvo. O responsável ficou associado ao seu usuário.');
      setFoto(null);
      setFotoAgua1(null);
      setFotoAgua2(null);
      setForm(vazio);
      await carregar(tipo);
    } catch (e) {
      const msg = e?.message || '';
      const codigo = e?.code || '';
      if (String(codigo).startsWith('storage/') || /storage|unauthorized|permission/i.test(msg)) {
        setErro('Não foi possível enviar a foto. Escolha a imagem de novo e salve outra vez.');
      } else {
        setErro(msg || 'Erro ao salvar.');
      }
    } finally {
      setSalvando(false);
    }
  };

  const campo = (label, children) => (
    <div className="block text-sm">
      <span className="font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Monitoramento</h1>
        <p className="text-gray-600">
          Procedimentos do dia a dia. Cada lançamento grava data, hora e quem preencheu
          {userData?.nome ? ` (${userData.nome})` : ''}.
        </p>
      </div>

      {isRole?.('superadmin') && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          O superadmin só grava registros dentro de uma empresa com o módulo liberado.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {TIPOS.map((t) => {
          const Icon = t.icon;
          const ativo = t.id === tipo;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTipo(t.id)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${
                ativo ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
        <p className="text-sm text-gray-600">{atual?.ajuda}</p>
        {erro && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{erro}</p>}
        {ok && <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">{ok}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {tipo === 'agua' && (
            <>
              {campo('Ponto de coleta', <input className={inputCls} value={form.ponto} onChange={(e) => set('ponto', e.target.value)} placeholder="Ex.: caixa d'água 1" />)}
              {campo('pH (6,0 a 9,5)', <input className={inputCls} value={form.ph} onChange={(e) => set('ph', e.target.value)} placeholder="6,0 a 9,5" />)}
              {campo('Cloro residual livre (0,2 a 5,0 mg/L)', <input className={inputCls} value={form.cloro} onChange={(e) => set('cloro', e.target.value)} placeholder="0,2 a 5,0" />)}
            </>
          )}
          {tipo === 'limpeza' && (
            <>
              {campo(
                'Momento',
                <select className={inputCls} value={form.momento} onChange={(e) => set('momento', e.target.value)}>
                  <option value="pre_operacional">Pré-operacional</option>
                  <option value="pos_operacional">Pós-operacional</option>
                  <option value="pos_manutencao">Após manutenção</option>
                </select>
              )}
              {campo(
                'Setor',
                <select
                  className={inputCls}
                  value={form.setor_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, setor_id: e.target.value, equipamento_id: '' }))}
                >
                  <option value="">Selecione</option>
                  {setores.map((s) => (
                    <option key={s.id} value={s.id}>{s.nome}</option>
                  ))}
                </select>
              )}
              {campo(
                'Máquina',
                <select className={inputCls} value={form.equipamento_id} onChange={(e) => set('equipamento_id', e.target.value)} disabled={!form.setor_id}>
                  <option value="">
                    {!form.setor_id
                      ? 'Escolha o setor primeiro'
                      : equipamentosDoSetor.length
                        ? 'Selecione a máquina'
                        : 'Este setor não tem máquina cadastrada'}
                  </option>
                  {equipamentosDoSetor.map((e) => (
                    <option key={e.id} value={e.id}>{e.apelido || e.nome}</option>
                  ))}
                </select>
              )}
              {campo('O que foi feito', <input className={inputCls} value={form.descricao} onChange={(e) => set('descricao', e.target.value)} />)}
              {campo('Como conferir', <input className={inputCls} value={form.como_conferir} onChange={(e) => set('como_conferir', e.target.value)} />)}
            </>
          )}
          {tipo === 'manutencao' && (
            <>
              {campo(
                'Classe',
                <select className={inputCls} value={form.classe} onChange={(e) => set('classe', e.target.value)}>
                  <option value="preventiva">Preventiva</option>
                  <option value="corretiva">Corretiva</option>
                </select>
              )}
              {campo(
                'Item',
                <select className={inputCls} value={form.item} onChange={(e) => set('item', e.target.value)}>
                  <option value="iluminacao">Iluminação</option>
                  <option value="ventilacao">Ventilação</option>
                  <option value="equipamento">Equipamento / máquina</option>
                  <option value="piso">Piso</option>
                  <option value="parede">Parede</option>
                  <option value="teto">Teto</option>
                </select>
              )}
              {campo(
                'Equipamento',
                <select className={inputCls} value={form.equipamento_id} onChange={(e) => set('equipamento_id', e.target.value)}>
                  <option value="">Se aplicável</option>
                  {equipamentos.map((e) => (
                    <option key={e.id} value={e.id}>{e.apelido || e.nome}</option>
                  ))}
                </select>
              )}
              {campo('Descrição', <input className={inputCls} value={form.descricao} onChange={(e) => set('descricao', e.target.value)} />)}
            </>
          )}
          {tipo === 'pragas' && (
            <>
              {campo('Local da isca / ponto', <input className={inputCls} value={form.local} onChange={(e) => set('local', e.target.value)} />)}
              {campo(
                'Presença',
                <select className={inputCls} value={form.presenca} onChange={(e) => set('presenca', e.target.value)}>
                  <option value="nao">Sem presença</option>
                  <option value="sim">Com presença</option>
                </select>
              )}
              {campo('Ação', <input className={inputCls} value={form.descricao} onChange={(e) => set('descricao', e.target.value)} />)}
            </>
          )}
          {tipo === 'temperatura' && (
            <>
              {campo(
                'Câmara ou termohigrômetro',
                <select
                  className={inputCls}
                  value={form.equipamento_id}
                  onChange={(e) => {
                    const eq = equipamentosTemperatura.find((item) => item.id === e.target.value);
                    setForm((prev) => ({
                      ...prev,
                      equipamento_id: e.target.value,
                      ponto: eq ? eq.apelido || eq.nome || eq.tipo_nome || '' : ''
                    }));
                  }}
                >
                  <option value="">Selecione</option>
                  {equipamentosTemperatura.map((e) => (
                    <option key={e.id} value={e.id}>{e.apelido || e.nome || e.tipo_nome}</option>
                  ))}
                </select>
              )}
              {equipamentosTemperatura.length === 0 && (
                <p className="text-sm text-amber-800 md:col-span-2">
                  Nenhum equipamento de câmara ou termohigrômetro encontrado. Cadastre em Equipamentos com esse tipo ou nome.
                </p>
              )}
              {campo('Temperatura', <input className={inputCls} value={form.valor} onChange={(e) => set('valor', e.target.value)} placeholder="°C" />)}
              {campo(
                'Turno',
                <select className={inputCls} value={form.turno} onChange={(e) => set('turno', e.target.value)}>
                  <option value="manha">Manhã</option>
                  <option value="tarde">Tarde</option>
                </select>
              )}
            </>
          )}
          {tipo === 'habitos' && (
            <>
              {campo('Colaborador', <input className={inputCls} value={form.colaborador} onChange={(e) => set('colaborador', e.target.value)} />)}
              {campo(
                'Asseio',
                <select className={inputCls} value={form.asseio} onChange={(e) => set('asseio', e.target.value)}>
                  <option value="sim">Adequado</option>
                  <option value="nao">Inadequado</option>
                </select>
              )}
              {campo(
                'Uniforme',
                <select className={inputCls} value={form.uniforme} onChange={(e) => set('uniforme', e.target.value)}>
                  <option value="sim">Adequado</option>
                  <option value="nao">Inadequado</option>
                </select>
              )}
            </>
          )}
          {tipo === 'certificados' && (
            <>
              {campo(
                'Serviço',
                <select className={inputCls} value={form.servico} onChange={(e) => set('servico', e.target.value)}>
                  <option value="calibracao">Calibração</option>
                  <option value="pragas">Controle de pragas</option>
                  <option value="caixa_dagua">Limpeza de caixa d’água</option>
                  <option value="microbiologico">Microbiológico</option>
                  <option value="outro">Outro</option>
                </select>
              )}
              {campo('Prestador', <input className={inputCls} value={form.fornecedor} onChange={(e) => set('fornecedor', e.target.value)} />)}
              {campo('Validade', <input type="date" className={inputCls} value={form.validade} onChange={(e) => set('validade', e.target.value)} />)}
            </>
          )}
          {campo(
            'Resultado',
            <select className={inputCls} value={form.conforme ? 'sim' : 'nao'} onChange={(e) => set('conforme', e.target.value === 'sim')}>
              <option value="sim">Conforme</option>
              <option value="nao">Não conforme</option>
            </select>
          )}
          {campo('Observação', <input className={inputCls} value={form.observacao} onChange={(e) => set('observacao', e.target.value)} />)}
          {tipo === 'agua' ? (
            <>
              {campo(
                'Foto 1 da análise',
                <>
                  <input className={inputCls} type="file" accept="image/*,application/pdf,.heic,.heif" onChange={(e) => setFotoAgua1(arquivoDaEscolha(e))} />
                  <PreviaFoto arquivo={fotoAgua1} />
                </>
              )}
              {campo(
                'Foto 2 da análise',
                <>
                  <input className={inputCls} type="file" accept="image/*,application/pdf,.heic,.heif" onChange={(e) => setFotoAgua2(arquivoDaEscolha(e))} />
                  <PreviaFoto arquivo={fotoAgua2} />
                </>
              )}
            </>
          ) : (
            campo(
              tipo === 'temperatura' ? 'Foto (obrigatória)' : 'Foto ou anexo',
              <input type="file" accept="image/*,.pdf" onChange={(e) => setFoto(e.target.files?.[0] || null)} />
            )
          )}
        </div>
        <button
          type="button"
          disabled={salvando}
          onClick={salvar}
          className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
        >
          {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
          Salvar registro
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-4 py-3 border-b font-medium text-gray-900">Lançamentos recentes</div>
        {loading ? (
          <p className="p-4 text-sm text-gray-500">Carregando…</p>
        ) : itens.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">Nenhum registro deste tipo ainda.</p>
        ) : (
          <ul className="divide-y">
            {itens.map((item) => (
              <li key={item.id} className="px-4 py-3 text-sm">
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-medium text-gray-900">
                    {item.conforme === false ? 'Não conforme' : 'Conforme'} · {item.usuario_nome || '—'}
                  </span>
                  <span className="text-gray-500">{formatarDataHoraBr(item.criado_em)}</span>
                </div>
                <p className="text-gray-600 mt-1">
                  {Object.entries(item.campos || {})
                    .filter(([k, v]) => v && !String(k).startsWith('faixa_'))
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(' · ')}
                </p>
                {item.observacao && <p className="text-gray-500 mt-1">{item.observacao}</p>}
                {(item.fotos?.length ? item.fotos : item.foto_url ? [item.foto_url] : []).map((url, index) => (
                  <a key={url} className="text-emerald-700 underline mr-3" href={url} target="_blank" rel="noreferrer">
                    Abrir anexo {index + 1}
                  </a>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MonitoramentoPage;
